import { Checkbox } from '@/global/components/Checkbox'
import Collapse from '@/global/components/Collapse'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { cn } from '@/lib/utils'
import { formatPorcentagemRegra } from '@acme/common'
import { SetAlwaysRequired } from '@acme/core'
import {
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
    FormFieldTime,
} from 'global/components/Form'
import { FormRadioGroup } from 'global/components/Form/RadioGroup'
import IconeAjuda from 'global/components/IconeAjuda'
import { CornerDownRightIcon } from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { PorcentagemRegraCalculo } from 'service/api'
import { match } from 'ts-pattern'
import VerticalDivider from '../../../../../global/components/Divider/VerticalDivider'
import { horasExtrasModel, IHorasExtras } from '../../constants'

type TipoHorasExcedentes = 'DIARIO' | 'SEMANAL' | 'MENSAL'
const DIAS_UTEIS = ['seg', 'ter', 'qua', 'qui', 'sex']

type Porcentagens = SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]

const opcoesPorcentagem = (porcentagens: Porcentagens, disabled?: boolean) =>
    porcentagens.map(item => ({
        value: item.porcentagem,
        label: formatPorcentagemRegra(item.porcentagem),
        disabled,
    }))

const OPCOES_PRIORIDADE = [
    { value: 'maior', label: 'Maior' },
    { value: 'menor', label: 'Menor' },
]

function Card({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) {
    return (
        <ContentGuide.Card
            className={cn(
                'h-fit flex-row max-w-full gap-2 rounded-sm',
                className,
            )}
        >
            {children}
        </ContentGuide.Card>
    )
}

export default function HorasExtras() {
    const { watch, getValues, setValue } = useFormContext()

    const [horasExtrasFeriado, pForm = [], configFeriado] = watch([
        'horasExtrasFeriado',
        'porcentagens',
        'horasExtras.fer',
    ]) as [boolean, PorcentagemRegraCalculo[], IHorasExtras]

    const porcentagens: SetAlwaysRequired<
        PorcentagemRegraCalculo,
        'porcentagem'
    >[] = pForm.filter(it => !!it.porcentagem) as any

    const tipoHorasExcedentes: TipoHorasExcedentes = watch(
        'tipoHorasExcedentes',
        'DIARIO',
    )

    useEffect(() => {
        if (!horasExtrasFeriado) {
            setValue('horasExtras.fer', {
                ...configFeriado,
                porcentagemDemaisHrs: null,
                porcentagemPrimeirasHrs: null,
            })
        }
    }, [porcentagens.map(p => p.porcentagem).join(', ')])

    const [editarSemana, setEditarSemana] = React.useState(() => {
        const semExists = Object.keys(getValues('horasExtras') ?? {}).includes(
            'sem',
        )

        return !semExists
    })

    const handleChange = (checked: boolean) => {
        setEditarSemana(checked)
    }

    const domingo = () => (
        <>
            <Grid item xs={30}>
                <HorizontalDivider className="my-4" />
            </Grid>
            <Grid item sm={5 + 3} xs={24 + 6}>
                <span className="text-nowrap text-sm font-semibold">
                    Domingo
                </span>
            </Grid>
            <Grid item sm={10} xs={30}>
                <Card>
                    <FormFieldTime
                        name={`horasExtras[dom].qtdPrimeirasHrs`}
                        label="Primeiras"
                        disabled={watch(`horasExtras[dom].todas`, false)}
                        required
                        unHelperText
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                h
                            </span>
                        }
                    />
                    <FormFieldSelect
                        required
                        name={`horasExtras[dom].porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                        options={opcoesPorcentagem(porcentagens)}
                    />
                </Card>
            </Grid>
            <Grid item sm={7} xs={10}>
                <Card>
                    <FormFieldSelect
                        required
                        name={`horasExtras[dom].porcentagemDemaisHrs`}
                        disabled={watch(`horasExtras[dom].todas`, false)}
                        label="Demais"
                        unHelperText
                        options={opcoesPorcentagem(porcentagens)}
                    />
                </Card>
            </Grid>
            <Grid item sm={5} xs={20}>
                <Card>
                    <FormFieldSelect
                        name={`horasExtras[dom].prioridadeCompensacao`}
                        required
                        label="Prioridade"
                        unHelperText
                        options={OPCOES_PRIORIDADE}
                    />
                </Card>
            </Grid>
        </>
    )

    return (
        <>
            <div className="flex justify-end items-end mb-2">
                <FormRadioGroup
                    row
                    name="tipoHorasExcedentes"
                    label="Configuração das horas extendidas"
                    defaultValue="DIARIO"
                    options={[
                        { value: 'DIARIO', label: 'Diário' },
                        { value: 'SEMANAL', label: 'Semanal' },
                        { value: 'MENSAL', label: 'Mensal' },
                    ]}
                />
            </div>
            {match(tipoHorasExcedentes)
                .with('DIARIO', () => (
                    <RenderHorasExtras
                        editarSemana={editarSemana}
                        porcentagem={porcentagens}
                        tipoHorasExcedentes={tipoHorasExcedentes}
                        handleChange={handleChange}
                    />
                ))
                .with('SEMANAL', () => (
                    <Grid container columns={30} className="items-center">
                        <Grid item sm={5 + 3} xs={24 + 6}>
                            <span className="text-nowrap text-sm font-semibold">
                                Semana
                            </span>
                        </Grid>
                        <Grid item sm={10} xs={30}>
                            <Card>
                                <FormField
                                    key="semanal-primeiras-horas"
                                    name={`horasExtras.semanal.qtdPrimeirasHrs`}
                                    label="Primeiras"
                                    required
                                    options={{
                                        numericOnly: true,
                                        blocks: [2, 2],
                                        delimiter: ':',
                                    }}
                                    unHelperText
                                    onBlur={() => {
                                        setValue(
                                            'horasExtras.semanal.qtdPrimeirasHrs',
                                            getValues<string>(
                                                'horasExtras.semanal.qtdPrimeirasHrs',
                                            ).padEnd(5, '0'),
                                        )
                                    }}
                                    endAdornment={
                                        <span className="mr-2 text-muted-foreground">
                                            h
                                        </span>
                                    }
                                />
                                <FormFieldSelect
                                    key="semanal-porcentagem-1"
                                    required
                                    name={`horasExtras.semanal.porcentagemPrimeirasHrs`}
                                    label="Será"
                                    unHelperText
                                    options={opcoesPorcentagem(porcentagens)}
                                />
                            </Card>
                        </Grid>
                        <Grid item sm={7} xs={10}>
                            <Card>
                                <FormFieldSelect
                                    key="semanal-porcentagem-2"
                                    required
                                    name={`horasExtras.semanal.porcentagemDemaisHrs`}
                                    label="Demais"
                                    unHelperText
                                    options={opcoesPorcentagem(porcentagens)}
                                />
                            </Card>
                        </Grid>
                        <Grid item sm={5} xs={20}>
                            <Card>
                                <FormFieldSelect
                                    key="semanal-prioridade"
                                    name={`horasExtras.semanal.prioridadeCompensacao`}
                                    required
                                    label="Prioridade"
                                    unHelperText
                                    options={OPCOES_PRIORIDADE}
                                />
                            </Card>
                        </Grid>
                        {domingo()}
                    </Grid>
                ))
                .with('MENSAL', () => (
                    <Grid container columns={30} className="items-center">
                        <Grid item sm={5 + 3} xs={24 + 6}>
                            <span className="text-nowrap font-semibold text-sm">
                                Mensal
                            </span>
                        </Grid>
                        <Grid item sm={10} xs={30}>
                            <Card>
                                <FormField
                                    key="mensal-primeiras"
                                    name={`horasExtras.mensal.qtdPrimeirasHrs`}
                                    label="Primeiras"
                                    required
                                    options={{
                                        numericOnly: true,
                                        blocks: [2, 2],
                                        delimiter: ':',
                                    }}
                                    unHelperText
                                    onBlur={() => {
                                        setValue(
                                            'horasExtras.mensal.qtdPrimeirasHrs',
                                            getValues<string>(
                                                'horasExtras.mensal.qtdPrimeirasHrs',
                                            ).padEnd(5, '0'),
                                        )
                                    }}
                                    endAdornment={
                                        <span className="mr-2 text-muted-foreground">
                                            h
                                        </span>
                                    }
                                />
                                <FormFieldSelect
                                    key="mensal-porcentagem-1"
                                    required
                                    name={`horasExtras.mensal.porcentagemPrimeirasHrs`}
                                    label="Será"
                                    unHelperText
                                    options={opcoesPorcentagem(porcentagens)}
                                />
                            </Card>
                        </Grid>
                        <Grid item sm={7} xs={10}>
                            <Card>
                                <FormFieldSelect
                                    key="mensal-porcentagem-2"
                                    required
                                    name={`horasExtras.mensal.porcentagemDemaisHrs`}
                                    label="Demais"
                                    unHelperText
                                    options={opcoesPorcentagem(porcentagens)}
                                />
                            </Card>
                        </Grid>
                        <Grid item sm={5} xs={20}>
                            <Card>
                                <FormFieldSelect
                                    key="mensal-prioridade"
                                    name={`horasExtras.mensal.prioridadeCompensacao`}
                                    required
                                    label="Prioridade"
                                    unHelperText
                                    options={OPCOES_PRIORIDADE}
                                />
                            </Card>
                        </Grid>
                        {domingo()}
                    </Grid>
                ))
                .exhaustive()}
            <HorizontalDivider className="my-4" />
            <FormFieldCheckbox
                name="horasExtrasFeriado"
                label="Horas extras no feriado"
                className="mt-4 mb-2 ml-auto flex w-fit"
                onChange={checked => {
                    if (checked)
                        setValue('horasExtras.fer', {
                            ...configFeriado,
                            porcentagemDemaisHrs: null,
                            porcentagemPrimeirasHrs: null,
                        })
                }}
                endAdornment={
                    <IconeAjuda>
                        Horas extras no feriado habilitadas por padrão.
                        Desabilite se não aplicável.
                    </IconeAjuda>
                }
            />
            <Linha
                disabled={!horasExtrasFeriado}
                item={horasExtrasModel.horasExtras.fer}
                tipoHorasExcedentes={tipoHorasExcedentes}
                editar={true}
                key={
                    horasExtrasFeriado
                        ? porcentagens.map(p => p.id).join(', ')
                        : 'fer'
                }
                index={'fer'}
                porcentagem={porcentagens}
            >
                <div className="w-full flex justify-end my-2">
                    <CheckboxControlePorTurno dia="fer" />
                </div>
            </Linha>
        </>
    )
}

function RenderHorasExtras({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (checked: boolean) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            {Object.entries(horasExtrasModel.horasExtras).map(
                ([key, item], index) =>
                    ['sem', 'sab', 'dom'].includes(key) && (
                        <React.Fragment key={key}>
                            {index !== 0 && (
                                <HorizontalDivider className="my-4" />
                            )}
                            <Linha
                                editar={index === 0 ? !editarSemana : true}
                                item={item}
                                index={key}
                                porcentagem={porcentagem}
                                tipoHorasExcedentes={tipoHorasExcedentes}
                            >
                                {key === 'sem' ? (
                                    <SemanaComponent
                                        editarSemana={editarSemana}
                                        handleChange={handleChange}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                    />
                                ) : (
                                    <div className="w-full flex justify-end my-2">
                                        <CheckboxControlePorTurno dia={key} />
                                    </div>
                                )}
                            </Linha>
                        </React.Fragment>
                    ),
            )}
        </>
    )
}

function SemanaComponent({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (checked: boolean) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            <div className="flex w-full justify-end items-center mt-4">
                <CheckboxControlePorTurno dia="sem" disabled={editarSemana} />
                <VerticalDivider className="ml-2 mr-6" />
                <Checkbox
                    id="editarSemana"
                    checked={editarSemana}
                    onCheckedChange={valor => handleChange(valor === true)}
                    label={
                        <>
                            Editar semana
                            <IconeAjuda className="ml-2 -mr-3">
                                <div className="w-52">
                                    Habilite para editar os dias de segunda a
                                    sexta-feira separadamente.
                                </div>
                            </IconeAjuda>
                        </>
                    }
                />
            </div>
            <Collapse open={editarSemana} className="w-full">
                <div className="flex flex-col gap-2 py-4 w-full">
                    {Object.entries(horasExtrasModel.horasExtras).map(
                        ([subKey, subItem]) =>
                            DIAS_UTEIS.includes(subKey) && (
                                <React.Fragment key={subKey}>
                                    <HorizontalDivider className="mb-4" />
                                    <Linha
                                        editar={editarSemana}
                                        item={subItem}
                                        index={subKey}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                        isChildren
                                    >
                                        <div className="w-full flex justify-end my-2">
                                            <CheckboxControlePorTurno
                                                dia={subKey}
                                            />
                                        </div>
                                    </Linha>
                                </React.Fragment>
                            ),
                    )}
                </div>
            </Collapse>
        </>
    )
}

interface LinhaProps {
    item: IHorasExtras
    index: string
    editar: boolean
    onChange?: (name: string, value: unknown) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    disabled?: boolean
    children?: React.ReactNode
    tipoHorasExcedentes: TipoHorasExcedentes
    isChildren?: boolean
}

const Linha: React.FC<LinhaProps> = ({
    item,
    index,
    editar,
    porcentagem,
    children,
    disabled = false,
    tipoHorasExcedentes,
    isChildren,
}) => {
    const { watch, setValue } = useFormContext()

    const isTodas = useCallback(
        (key: string) => {
            return watch(`horasExtras[${key}].todas`, false)
        },
        [watch],
    )

    const isTurno = useCallback(
        (index: string) => {
            return watch(`horasExtras[${index}].controleTurno`, false)
        },
        [watch],
    )

    function handleChangeSemana(field: string, value: any) {
        if (index === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    const isFeriadoNaoDiario =
        index === 'fer' && tipoHorasExcedentes !== 'DIARIO'

    return (
        <Grid container columns={30} className="items-center">
            <Grid item sm={5} xs={24}>
                <span
                    className={cn(
                        'text-nowrap inline-flex gap-1',
                        'items-start text-sm font-semibold',
                        { 'line-through': !editar },
                    )}
                >
                    {isChildren && <CornerDownRightIcon className="size-4" />}
                    {item.nome}
                </span>
            </Grid>
            <Grid item sm={3} xs={6}>
                {isFeriadoNaoDiario ? (
                    <div className="w-full" />
                ) : (
                    <FormFieldCheckbox
                        name={`horasExtras[${index}].todas`}
                        label="Todas"
                        disabled={!editar || disabled}
                        onChange={marcado => {
                            handleChangeSemana('todas', marcado)
                            if (marcado) {
                                setValue(
                                    `horasExtras[${index}].porcentagemDemaisHrs`,
                                    null,
                                )
                                setValue(
                                    `horasExtras[${index}].qtdPrimeirasHrs`,
                                    '',
                                )
                                handleChangeSemana('porcentagemDemaisHrs', null)
                                handleChangeSemana('qtdPrimeirasHrs', '')

                                setValue(
                                    `horasExtras[${index}].controleTurno`,
                                    false,
                                )
                                handleChangeSemana('controleTurno', false)
                            }
                        }}
                    />
                )}
            </Grid>
            <Grid item sm={10} xs={30}>
                <Card>
                    <FormFieldTime
                        disabled={
                            !editar ||
                            isTodas(index) ||
                            disabled ||
                            isTurno(index)
                        }
                        name={`horasExtras[${index}].qtdPrimeirasHrs`}
                        label={isTurno(index) ? 'Primeiro turno' : 'Primeiras'}
                        required={!isTodas(index) && !isTurno(index)}
                        unHelperText
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                h
                            </span>
                        }
                        onChange={hora => {
                            handleChangeSemana('qtdPrimeirasHrs', hora)
                        }}
                    />
                    <FormFieldSelect
                        required
                        name={`horasExtras[${index}].porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                        disabled={!editar || disabled}
                        options={opcoesPorcentagem(porcentagem, disabled)}
                        onChange={valor => {
                            handleChangeSemana('porcentagemPrimeirasHrs', valor)
                        }}
                    />
                </Card>
            </Grid>
            <Grid item sm={7} xs={10}>
                <Card>
                    <FormFieldSelect
                        disabled={!editar || isTodas(index) || disabled}
                        required={!isTodas(index)}
                        name={`horasExtras[${index}].porcentagemDemaisHrs`}
                        label={isTurno(index) ? 'Segundo turno' : 'Demais'}
                        unHelperText
                        options={opcoesPorcentagem(porcentagem)}
                        onChange={valor => {
                            handleChangeSemana('porcentagemDemaisHrs', valor)
                        }}
                    />
                </Card>
            </Grid>
            <Grid item sm={5} xs={20}>
                <Card>
                    <FormFieldSelect
                        name={`horasExtras[${index}].prioridadeCompensacao`}
                        required
                        label="Prioridade"
                        unHelperText
                        disabled={!editar || disabled}
                        options={OPCOES_PRIORIDADE}
                        onChange={valor => {
                            handleChangeSemana('prioridadeCompensacao', valor)
                        }}
                    />
                </Card>
            </Grid>
            {/* Filho solto viraria uma trilha de 1/30 da linha — a caixa de
                seleção do rodapé precisa da linha inteira. */}
            {children && (
                <Grid item xs={30}>
                    {children}
                </Grid>
            )}
        </Grid>
    )
}

function CheckboxControlePorTurno({
    dia,
    disabled,
}: {
    dia: string
    disabled?: boolean
}) {
    const { watch, setValue } = useFormContext()
    const todas = watch(`horasExtras[${dia}].todas`, false)

    function handleChangeSemana(field: string, value: any) {
        if (dia === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    return (
        <FormFieldCheckbox
            label="Controle por turno"
            name={`horasExtras[${dia}].controleTurno`}
            disabled={disabled || todas}
            onChange={marcado => {
                handleChangeSemana('controleTurno', marcado)
            }}
            endAdornment={
                <IconeAjuda className="-ml-2">
                    {todas && [
                        'Desabilite a opção "Todas" para alterar.',
                        <br />,
                    ]}
                    Se habilitado, o controle de horas extras será feito por
                    turno (manhã e tarde) ao invés de primeiras e demais horas.
                    <br />A divisão de turnos ocorre ao meio-dia.
                </IconeAjuda>
            }
        />
    )
}
